import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, Eye, Lock, UserCheck, FileText, Clock } from "lucide-react";

export default function Privacy() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className={`mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {t('privacy.backToHome')}
          </Button>
          
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('privacy.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
            {t('privacy.subtitle')}
          </p>
          
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-4 w-4" />
            {t('privacy.lastUpdated')}: {t('privacy.updateDate')}
          </div>
        </div>

        <div className="space-y-6">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t('privacy.introduction.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('privacy.introduction.content')}
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {t('privacy.dataCollection.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">{t('privacy.dataCollection.personalInfo.title')}</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                  <li>{t('privacy.dataCollection.personalInfo.phoneNumber')}</li>
                  <li>{t('privacy.dataCollection.personalInfo.fullName')}</li>
                  <li>{t('privacy.dataCollection.personalInfo.email')}</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">{t('privacy.dataCollection.financialData.title')}</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                  <li>{t('privacy.dataCollection.financialData.transactions')}</li>
                  <li>{t('privacy.dataCollection.financialData.categories')}</li>
                  <li>{t('privacy.dataCollection.financialData.payments')}</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">{t('privacy.dataCollection.technicalData.title')}</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                  <li>{t('privacy.dataCollection.technicalData.deviceInfo')}</li>
                  <li>{t('privacy.dataCollection.technicalData.usage')}</li>
                  <li>{t('privacy.dataCollection.technicalData.logs')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Your Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                {t('privacy.dataUsage.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                <li>{t('privacy.dataUsage.provideServices')}</li>
                <li>{t('privacy.dataUsage.improveServices')}</li>
                <li>{t('privacy.dataUsage.communication')}</li>
                <li>{t('privacy.dataUsage.security')}</li>
                <li>{t('privacy.dataUsage.legal')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t('privacy.security.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('privacy.security.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('privacy.security.encryption')}</li>
                <li>{t('privacy.security.access')}</li>
                <li>{t('privacy.security.monitoring')}</li>
                <li>{t('privacy.security.backups')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.sharing.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                {t('privacy.sharing.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('privacy.sharing.serviceProviders')}</li>
                <li>{t('privacy.sharing.legal')}</li>
                <li>{t('privacy.sharing.businessTransfer')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                {t('privacy.rights.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                <li>{t('privacy.rights.access')}</li>
                <li>{t('privacy.rights.correction')}</li>
                <li>{t('privacy.rights.deletion')}</li>
                <li>{t('privacy.rights.portability')}</li>
                <li>{t('privacy.rights.withdraw')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('privacy.retention.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('privacy.retention.description')}
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.contact.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                {t('privacy.contact.description')}
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="font-semibold">FINGENT</p>
                <p className="text-gray-600 dark:text-gray-400">fingentlife@gmail.com</p>
                <p className="text-gray-600 dark:text-gray-400">{t('privacy.contact.location')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Policy Changes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('privacy.changes.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('privacy.changes.description')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 